// src/pages/clab-biodata-pending/index.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import HeaderBar from "components/ui/HeaderBar";
import RoleBasedSidebar from "components/ui/RoleBasedSidebar";
import BreadcrumbNavigation from "components/ui/BreadcrumbNavigation";
import StatusIndicatorBadge from "components/ui/StatusIndicatorBadge";
import Icon from "components/AppIcon";
import Image from "components/AppImage";
import { clabAPI, biodataAPI } from "utils/api";
import { useBiodataCache } from "context/BiodataCacheContext";
import PaginationComponent from "components/PaginationComponent";
// import { biodataService, auditService } from 'utils/supabaseServices';
// TODO: Migrate biodataService and auditService usages to backend API
import { useAuth } from "context/AuthContext";
import { getUserRoleForComponents } from "utils/authHelpers";
import BiodataDetailsView from "components/BiodataDetailsView";
import FilterPanel from "./components/FilterPanel";
import BulkActionPanel from "./components/BulkActionPanel";
import RejectionModal from "./components/RejectionModal";
import Notifier from "components/ui/Notifier";
import { useNotifications } from "hooks/useNotifications";
import { useBiodataManagement } from "hooks/useBiodataManagement";
import {
  statusToText,
  APP_STATUS,
  getStatusBadgeClass,
} from "../../constants/statusUtils";

const ClabBiodataPending = () => {
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const userRole = getUserRoleForComponents(user);
  const {
    getBiodataByStatus,
    invalidateOnUpdate,
    getPhotoUrl,
    prefetchPhotoUrl,
  } = useBiodataCache();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [biodataList, setBiodataList] = useState([]);
  const [biodataPagination, setBiodataPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedBiodata, setSelectedBiodata] = useState([]);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedBiodataDetails, setSelectedBiodataDetails] = useState(null);
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [rejectionType, setRejectionType] = useState("single"); // 'single' or 'bulk'
  const [singleRejectId, setSingleRejectId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [filters, setFilters] = useState({
    search: "",
    nationality: "",
    recruiter_id: "",
    date_from: "",
    date_to: "",
    skill: "",
  });
  const [exportScope, setExportScope] = useState("all");
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [approveTargetId, setApproveTargetId] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false); // New state for action buttons
  const [isBulkUpdating, setIsBulkUpdating] = useState(false); // Separate state for bulk actions
  const [showRejectConfirmModal, setShowRejectConfirmModal] = useState(false);
  const [pendingReject, setPendingReject] = useState(null);
  const {
    notifications,
    showSuccess,
    showError,
    removeNotification: dismissNotification,
  } = useNotifications();

  const { exportBiodata, exportStatus } = useBiodataManagement({
    apiEndpoints: {
      list: "/biodatas/status/pending",
      export: "/biodatas/export",
      print: "/biodatas/print",
    },
    biodataType: "pending",
    enableDataFetching: false, // Don't fetch data, we already have it
  });

  useEffect(() => {
    let isMounted = true;

    const loadPendingBiodata = async () => {
      try {
        setLoading(true);
        setError(null);
        // Build query params from filters and currentPage
        const params = {
          page: currentPage,
          per_page: itemsPerPage,
          search: filters.search || undefined,
          nationality: filters.nationality || undefined,
          recruiter_id: filters.recruiter_id || undefined,
          date_from: filters.date_from || undefined,
          date_to: filters.date_to || undefined,
          skill: filters.skill || undefined,
        };

        // Use cache instead of direct API call
        const result = await getBiodataByStatus("pending", params);

        if (result.success) {
          const items = Array.isArray(result.records)
            ? result.records
            : Array.isArray(result.data?.data)
            ? result.data.data
            : [];
          setBiodataList(items);
          setBiodataPagination(result.pagination);

          // Log cache status for debugging
          if (result.fromCache) {
          } else {
          }
        } else {
          setError(
            result.error || result.message || "Failed to load pending biodata"
          );
        }
      } catch (error) {
        setError("Failed to load pending biodata");
      } finally {
        setLoading(false);
      }
    };

    loadPendingBiodata();

    return () => {
      isMounted = false;
    };
  }, [filters, currentPage, itemsPerPage, getBiodataByStatus]);

  // When filters change, reset to first page
  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  // When items per page changes, reset to first page
  useEffect(() => {
    setCurrentPage(1);
  }, [itemsPerPage]);

  // Progressive photo prefetch when avatars enter viewport
  useEffect(() => {
    const elements = document.querySelectorAll("[data-biodata-id]");
    if (!elements || elements.length === 0) return;
    const seen = new Set();
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const idAttr = entry.target.getAttribute("data-biodata-id");
          const id = Number(idAttr) || idAttr;
          if (!id || seen.has(id)) return;
          const rec = biodataList.find((b) => b.id === id);
          const direct = rec?.photo_url || rec?.primary_photo_url;
          const cached = getPhotoUrl(id, rec);
          if (!direct && !cached) prefetchPhotoUrl(id);
          seen.add(id);
          observer.unobserve(entry.target);
        });
      },
      { root: null, threshold: 0.25 }
    );
    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [biodataList, getPhotoUrl, prefetchPhotoUrl]);

  // Redirect if not CLAB user
  useEffect(() => {
    if (
      userProfile &&
      !["clab_user", "clab", "admin", "master_admin", "1", 1].includes(
        userProfile.role
      )
    ) {
      navigate("/login-screen");
    }
  }, [userProfile, navigate]);

  // Use backend pagination
  const paginatedBiodata = biodataList;
  const totalPages = biodataPagination ? biodataPagination.total_pages : 1;

  // Pagination controls
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleSelectBiodata = (biodataId) => {
    setSelectedBiodata((prev) =>
      prev.includes(biodataId)
        ? prev.filter((id) => id !== biodataId)
        : [...prev, biodataId]
    );
  };

  const handleSelectAll = () => {
    if (selectedBiodata.length === paginatedBiodata.length) {
      setSelectedBiodata([]);
    } else {
      setSelectedBiodata(paginatedBiodata.map((b) => b.id));
    }
  };

  const handleViewDetails = async (biodata) => {
    try {
      const result = await biodataAPI.getBiodataById(biodata.id);
      setSelectedBiodataDetails(result.data);
      setShowDetailsModal(true);
    } catch (error) {
      setError("Failed to load biodata details");
    }
  };

  const handleApprove = (biodataId, notes = "Approved by CLAB user") => {
    setApproveTargetId(biodataId);
    setShowApproveModal(true);
  };

  const confirmApprove = async () => {
    setIsUpdating(true); // Set loading only after user confirms
    try {
      await clabAPI.approveBiodata(approveTargetId);

      // Invalidate cache immediately
      invalidateOnUpdate([approveTargetId], "pending", "approved");

      setBiodataList((prev) => prev.filter((b) => b.id !== approveTargetId));
      setSelectedBiodata((prev) => prev.filter((id) => id !== approveTargetId));
      showSuccess("The biodata is Approved");
      // Close details modal after successful approval
      setShowDetailsModal(false);
      setSelectedBiodataDetails(null);
    } catch (error) {
      showError("Failed to approve biodata");
    } finally {
      setShowApproveModal(false);
      setApproveTargetId(null);
      setIsUpdating(false);
    }
  };

  const handleReject = async (
    biodataIds,
    { reason_id, reason_ids, comments }
  ) => {
    setPendingReject({ ids: biodataIds, reason_id, reason_ids, comments });
    setShowRejectConfirmModal(true);
  };

  const confirmReject = async () => {
    setIsUpdating(true); // Set loading only after user confirms
    if (!pendingReject) return;
    const { ids, reason_id, reason_ids, comments } = pendingReject;
    const payload = {
      reason_ids: reason_ids || (reason_id ? [reason_id] : []), // Support both formats
      comments,
    };
    try {
      if (Array.isArray(ids)) {
        // Invalidate cache for all rejected biodatas
        invalidateOnUpdate(ids, "pending", "rejected");

        await Promise.all(ids.map((id) => clabAPI.rejectBiodata(id, payload)));
      } else {
        await clabAPI.rejectBiodata(ids, payload);
      }
      setBiodataList((prev) =>
        prev.filter((b) => ![].concat(ids).includes(b.id))
      );
      setSelectedBiodata((prev) =>
        prev.filter((id) => ![].concat(ids).includes(id))
      );
      showSuccess("The biodata is Rejected");
      // Close details modal after successful rejection
      setShowDetailsModal(false);
      setSelectedBiodataDetails(null);
    } catch (error) {
      showError("Failed to reject biodata");
    } finally {
      setShowRejectConfirmModal(false);
      setPendingReject(null);
      setShowRejectionModal(false);
      setSingleRejectId(null);
      setIsUpdating(false);
    }
  };

  const handleBulkApprove = async (biodataIds) => {
    setIsBulkUpdating(true);
    try {
      await Promise.all(biodataIds.map((id) => clabAPI.approveBiodata(id)));

      // Invalidate cache for bulk approve
      invalidateOnUpdate(biodataIds, "pending", "approved");

      setBiodataList((prev) => prev.filter((b) => !biodataIds.includes(b.id)));
      setSelectedBiodata([]);
      showSuccess(`${biodataIds.length} biodata(s) approved successfully`);
    } catch (error) {
      showError("Failed to bulk approve biodata");
    } finally {
      setIsBulkUpdating(false);
    }
  };

  const handleBulkReject = () => {
    setRejectionType("bulk");
    setShowRejectionModal(true);
  };

  const handleSingleReject = (biodataId) => {
    setSingleRejectId(biodataId);
    setRejectionType("single");
    setShowRejectionModal(true);
  };

  const handleExportData = async () => {
    try {
      if (
        exportScope === "current_page" &&
        (!biodataList || biodataList.length === 0)
      ) {
        showError("No pending biodata on the current page to export");
        return;
      }

      const exportFilters = exportScope === "filtered" ? filters : undefined;
      const pagination =
        exportScope === "current_page"
          ? { page: currentPage, perPage: itemsPerPage }
          : undefined;

      await exportBiodata([], "excel", {
        statusFilter: exportScope === "all" ? "all" : "pending",
        includeDocuments: false,
        includePhotos: false,
        useBackend: true,
        exportScope,
        filters: exportFilters,
        pagination,
      });
    } catch (error) {
      console.error("Export error:", error);
      showError(
        "Failed to export pending biodata: " +
          (error.message || "Unknown error")
      );
    }
  };

  const rejectionReasons = [
    "Expired medical certificate",
    "Data mismatch in documents",
    "Worker over age limit",
    "Incomplete documentation",
    "Invalid passport information",
    "Skill certificate expired",
    "Duplicate entry found",
    "Other",
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <HeaderBar
          userRole={userRole}
          userName={userProfile?.full_name || "CLAB User"}
          onSidebarToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
        />

        <RoleBasedSidebar
          userRole={userRole}
          isCollapsed={sidebarCollapsed}
          onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
        />

        <main
          className={`transition-all duration-300 pt-16 ${
            sidebarCollapsed ? "lg:ml-16" : "lg:ml-60"
          }`}
        >
          <div className="p-6 flex items-center justify-center min-h-[60vh]">
            <div className="text-center">
              <Icon
                name="Loader2"
                size={48}
                className="animate-spin text-primary mx-auto mb-4"
              />
              <p className="text-text-secondary">Loading pending biodata...</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <HeaderBar
        userRole={userRole}
        userName={userProfile?.full_name || "CLAB User"}
        onSidebarToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
      />
      <RoleBasedSidebar
        userRole={userRole}
        isCollapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
      />
      <main
        className={`transition-all duration-300 pt-16 ${
          sidebarCollapsed ? "lg:ml-16" : "lg:ml-60"
        }`}
      >
        <div className="p-6">
          <BreadcrumbNavigation userRole={userRole} />

          <div className="mb-8">
            <h1 className="text-3xl font-bold text-text-primary mb-2">
              Pending Biodata Review
            </h1>
            <p className="text-text-secondary">
              Review and approve foreign worker biodata submissions
            </p>
          </div>

          {error && (
            <div className="bg-error-50 border border-error-200 rounded-lg p-4 mb-6">
              <div className="flex items-center space-x-2">
                <Icon name="AlertCircle" size={20} className="text-error" />
                <h3 className="font-semibold text-error-800">Error</h3>
              </div>
              <p className="text-error-700 mt-2">{error}</p>
              <button
                onClick={() => setError(null)}
                className="mt-3 text-sm text-error-600 hover:text-error-800 font-medium"
              >
                Dismiss
              </button>
            </div>
          )}

          {/* Global Notifier for success/error toasts */}
          <Notifier
            type={
              notifications.length > 0
                ? notifications[notifications.length - 1].type
                : "info"
            }
            message={
              notifications.length > 0
                ? notifications[notifications.length - 1].message
                : ""
            }
            show={notifications.length > 0}
            onClose={() =>
              dismissNotification(notifications[notifications.length - 1]?.id)
            }
            duration={
              notifications.length > 0
                ? notifications[notifications.length - 1].duration
                : 3000
            }
          />

          <div className="space-y-6">
            {/* Filter Panel */}
            <FilterPanel filters={filters} onFiltersChange={setFilters} />

            {/* Bulk Action Panel */}
            {selectedBiodata.length > 0 && (
              <BulkActionPanel
                selectedCount={selectedBiodata.length}
                onApprove={() => handleBulkApprove(selectedBiodata)}
                onReject={handleBulkReject}
                disabled={isBulkUpdating || isUpdating} // Disable during any update operation
              />
            )}

            {/* Biodata Table */}
            <div className="card">
              <div className="p-6 border-b border-secondary-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-semibold text-text-primary">
                    Pending Reviews (
                    {biodataPagination ? biodataPagination.total : 0})
                  </h2>
                  <div className="flex items-center space-x-4">
                    <div className="flex flex-col items-end space-y-2">
                      <div className="flex items-center space-x-2">
                        <select
                          value={exportScope}
                          onChange={(e) => setExportScope(e.target.value)}
                          className="px-3 py-2 text-sm border border-secondary-300 rounded-md bg-surface focus:outline-none focus:ring-2 focus:ring-primary-300"
                        >
                          <option value="all">
                            All records (all statuses)
                          </option>
                          <option value="filtered">
                            Apply current filters
                          </option>
                          <option value="current_page">
                            Current page only
                          </option>
                        </select>
                        <button
                          onClick={handleExportData}
                          disabled={exportStatus.isExporting}
                          className="flex items-center space-x-2 px-4 py-2 text-secondary-700 bg-surface border border-secondary-300 rounded-md hover:bg-secondary-50 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <Icon name="Download" size={16} />
                          <span>
                            {exportStatus.isExporting
                              ? "Exporting..."
                              : "Export"}
                          </span>
                        </button>
                      </div>
                      {exportStatus.isExporting ||
                      exportStatus.stage === "downloading" ? (
                        <p className="text-xs text-text-secondary">
                          Exporting {exportStatus.totalRecords || "records"}
                          {exportStatus.estimatedSeconds
                            ? ` (~${Math.max(
                                1,
                                Math.round(exportStatus.estimatedSeconds)
                              )}s)`
                            : ""}{" "}
                          {exportStatus.downloadedBytes
                            ? `• ${
                                Math.round(
                                  (exportStatus.downloadedBytes / 1024 / 1024) *
                                    100
                                ) / 100
                              } MB downloaded`
                            : ""}
                        </p>
                      ) : exportStatus.stage === "completed" ? (
                        <p className="text-xs text-success-600">
                          Last export: {exportStatus.totalRecords || 0} records
                          → {exportStatus.filename || "download"}
                        </p>
                      ) : exportStatus.stage === "error" ? (
                        <p className="text-xs text-error-600">
                          Export failed: {exportStatus.error}
                        </p>
                      ) : null}
                    </div>
                    <button
                      onClick={() => window.location.reload()}
                      className="flex items-center space-x-2 px-4 py-2 text-secondary-700 bg-surface border border-secondary-300 rounded-md hover:bg-secondary-50 transition-colors duration-150"
                    >
                      <Icon name="RefreshCw" size={16} />
                      <span>Refresh</span>
                    </button>
                  </div>
                </div>
              </div>

              {paginatedBiodata.length === 0 ? (
                <div className="p-12 text-center">
                  <Icon
                    name="FileText"
                    size={48}
                    className="text-secondary-300 mx-auto mb-4"
                  />
                  <h3 className="text-lg font-medium text-text-primary mb-2">
                    No Pending Biodata
                  </h3>
                  <p className="text-text-secondary">
                    All biodata submissions have been reviewed.
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-secondary-50 border-b border-secondary-200">
                      <tr>
                        <th className="px-6 py-3">
                          <input
                            type="checkbox"
                            checked={
                              selectedBiodata.length ===
                                paginatedBiodata.length &&
                              paginatedBiodata.length > 0
                            }
                            onChange={handleSelectAll}
                            className="rounded border-secondary-300 text-primary focus:ring-primary"
                            title="Select all on this page"
                          />
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">
                          Worker No
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">
                          Name
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">
                          Passport No
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">
                          Nationality
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">
                          Skills
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">
                          Registered Date
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">
                          Medical Expiry
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">
                          VDR Type
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">
                          Submission Info
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-surface divide-y divide-secondary-200">
                      {paginatedBiodata.map((biodata) => (
                        <tr
                          key={biodata.id}
                          className="hover:bg-secondary-50 transition-colors duration-150"
                        >
                          <td className="px-6 py-4">
                            <input
                              type="checkbox"
                              checked={selectedBiodata.includes(biodata.id)}
                              onChange={() => handleSelectBiodata(biodata.id)}
                              className="rounded border-secondary-300 text-primary focus:ring-primary"
                            />
                          </td>
                          <td className="px-6 py-4 font-mono text-xs">
                            {biodata.worker_id}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-3">
                              <div
                                className="w-10 h-10 rounded-full overflow-hidden bg-secondary-100"
                                data-biodata-id={biodata.id}
                              >
                                <Image
                                  src={getPhotoUrl(biodata.id, biodata)}
                                  alt={biodata.full_name}
                                  className="w-full h-full object-cover"
                                />
                              </div>
                              <div>
                                <div className="text-sm text-text-primary">
                                  {biodata.full_name}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-xs">
                            {biodata.passport_no || "-"}
                          </td>
                          <td className="px-6 py-4 text-xs">
                            {biodata.nationality || "-"}
                          </td>
                          <td className="px-6 py-4 text-xs">
                            {Array.isArray(biodata.skills)
                              ? biodata.skills.join(", ")
                              : biodata.skills || biodata.skill || "-"}
                          </td>
                          <td className="px-6 py-4 text-xs">
                            {biodata.created_at
                              ? new Date(
                                  biodata.created_at
                                ).toLocaleDateString()
                              : "-"}
                          </td>
                          <td className="px-6 py-4 text-xs">
                            {biodata.medical_expiry_date
                              ? new Date(
                                  biodata.medical_expiry_date
                                ).toLocaleDateString()
                              : "-"}
                          </td>
                          <td className="px-6 py-4">
                            {biodata.vdr_type && (
                              <span className="inline-flex items-center px-2 py-1 bg-accent-100 text-accent-800 rounded-full text-xs font-medium">
                                <Icon
                                  name="Briefcase"
                                  size={12}
                                  className="mr-1"
                                />
                                {biodata.vdr_type}
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 text-xs">
                            {biodata.recruiter?.name || "Unknown Recruiter"}
                          </td>
                          <td className="px-6 py-4">
                            <StatusIndicatorBadge
                              status={biodata.status}
                              size="sm"
                            />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex items-center justify-end space-x-2">
                              <button
                                onClick={() => handleViewDetails(biodata)}
                                disabled={isUpdating || isBulkUpdating}
                                className="p-2 text-accent hover:bg-accent-50 rounded-md transition-colors duration-150 disabled:opacity-50"
                                title="View Details"
                              >
                                <Icon name="Eye" size={16} />
                              </button>
                              <button
                                onClick={() => handleApprove(biodata.id)}
                                disabled={isUpdating || isBulkUpdating}
                                className="p-2 text-success hover:bg-success-50 rounded-md transition-colors duration-150 disabled:opacity-50"
                                title="Approve"
                              >
                                {isUpdating &&
                                approveTargetId === biodata.id ? (
                                  <Icon
                                    name="Loader2"
                                    className="animate-spin"
                                    size={16}
                                  />
                                ) : (
                                  <Icon name="CheckCircle" size={16} />
                                )}
                              </button>
                              <button
                                onClick={() => handleSingleReject(biodata.id)}
                                disabled={isUpdating || isBulkUpdating}
                                className="p-2 text-error hover:bg-error-50 rounded-md transition-colors duration-150 disabled:opacity-50"
                                title="Reject"
                              >
                                {isUpdating && singleRejectId === biodata.id ? (
                                  <Icon
                                    name="Loader2"
                                    className="animate-spin"
                                    size={16}
                                  />
                                ) : (
                                  <Icon name="XCircle" size={16} />
                                )}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Pagination Component */}
              {biodataPagination && (
                <div className="px-6 py-4 border-t border-secondary-200">
                  <PaginationComponent
                    pagination={{
                      currentPage: biodataPagination.page || currentPage,
                      perPage: biodataPagination.limit || itemsPerPage,
                      total: biodataPagination.total || 0,
                      totalPages:
                        biodataPagination.totalPages ||
                        Math.ceil(
                          (biodataPagination.total || 0) /
                            (biodataPagination.limit || itemsPerPage)
                        ),
                      from:
                        ((biodataPagination.page || currentPage) - 1) *
                          (biodataPagination.limit || itemsPerPage) +
                        1,
                      to: Math.min(
                        (biodataPagination.page || currentPage) *
                          (biodataPagination.limit || itemsPerPage),
                        biodataPagination.total || 0
                      ),
                    }}
                    onPageChange={setCurrentPage}
                    onPerPageChange={setItemsPerPage}
                    showPerPageSelector={true}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Biodata Details Overlay using shared viewer */}
      {showDetailsModal && selectedBiodataDetails && (
        <>
          <style>{`
            @media print {
              .biodata-modal-overlay {
                display: none !important;
              }
              .biodata-modal-content {
                position: static !important;
                max-width: 100% !important;
                max-height: none !important;
                box-shadow: none !important;
              }
            }
          `}</style>
          <div className="biodata-modal-overlay fixed inset-0 z-1001 bg-secondary-900 bg-opacity-50 flex items-center justify-center p-4">
            <div className="biodata-modal-content bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-secondary-200 flex items-center justify-between">
                <h2 className="text-xl font-bold">Pending Biodata Details</h2>
                <button
                  onClick={() => {
                    setShowDetailsModal(false);
                    setSelectedBiodataDetails(null);
                  }}
                  className="text-secondary-600 hover:text-secondary-900"
                >
                  Close
                </button>
              </div>
              <div className="p-6">
                <BiodataDetailsView biodata={selectedBiodataDetails} />
              </div>
              <div className="p-6 border-t border-secondary-200 flex items-center justify-end gap-3">
                <button
                  onClick={() => handleSingleReject(selectedBiodataDetails.id)}
                  className="px-4 py-2 bg-error text-white rounded"
                >
                  Reject
                </button>
                <button
                  onClick={() => handleApprove(selectedBiodataDetails.id)}
                  className="px-4 py-2 bg-success text-white rounded"
                >
                  Approve
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Rejection Modal */}
      {showRejectionModal && (
        <RejectionModal
          type={rejectionType}
          biodataIds={
            rejectionType === "single" ? singleRejectId : selectedBiodata
          }
          rejectionReasons={rejectionReasons}
          onClose={() => {
            setShowRejectionModal(false);
            setSingleRejectId(null);
            setIsUpdating(false); // Clear loading state when modal is closed
          }}
          onConfirm={(reasonObj) =>
            handleReject(
              rejectionType === "single" ? singleRejectId : selectedBiodata,
              reasonObj
            )
          }
        />
      )}

      {/* Approve Confirmation Modal */}
      {showApproveModal && (
        <div className="fixed inset-0 bg-secondary-900 bg-opacity-50 flex items-center justify-center z-[1300] p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-6">
              <h2 className="text-xl font-bold mb-4">Confirm Approval</h2>
              <p>
                Are you sure you want to approve this biodata? This action
                cannot be undone.
              </p>
              <div className="flex justify-end mt-6 space-x-3">
                <button
                  onClick={() => {
                    setShowApproveModal(false);
                    setApproveTargetId(null);
                    setIsUpdating(false); // Clear loading state on cancel
                  }}
                  disabled={isUpdating}
                  className="px-4 py-2 bg-secondary-100 rounded disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmApprove}
                  disabled={isUpdating}
                  className="px-4 py-2 bg-success text-white rounded disabled:bg-success-400 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  {isUpdating && (
                    <Icon name="Loader2" className="animate-spin" size={16} />
                  )}
                  <span>{isUpdating ? "Approving..." : "Approve"}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reject Confirmation Modal */}
      {showRejectConfirmModal && (
        <div className="fixed inset-0 bg-secondary-900 bg-opacity-50 flex items-center justify-center z-[1300] p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-6">
              <h2 className="text-xl font-bold mb-4">Confirm Rejection</h2>
              <p>
                Are you sure you want to reject this biodata? This action cannot
                be undone.
              </p>
              <div className="flex justify-end mt-6 space-x-3">
                <button
                  onClick={() => {
                    setShowRejectConfirmModal(false);
                    setPendingReject(null);
                    setIsUpdating(false); // Clear loading state on cancel
                  }}
                  disabled={isUpdating}
                  className="px-4 py-2 bg-secondary-100 rounded disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmReject}
                  disabled={isUpdating}
                  className="px-4 py-2 bg-error text-white rounded disabled:bg-error-400 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  {isUpdating && (
                    <Icon name="Loader2" className="animate-spin" size={16} />
                  )}
                  <span>{isUpdating ? "Rejecting..." : "Reject"}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
export default ClabBiodataPending;
